import { liczbaZPola } from '../../config/klienci'
import { nazwaKlienta } from '../pme2/teczka'
import { saveKit, przypiszZestaw } from '../../api/account'
import { energiaKlienta, mocZZuzycia, rocznyKwh } from './energia'

/**
 * Asystent doboru w trybie „dla klienta” (prośba właściciela 2026-10-11). Z karty klienta firma wchodzi
 * w zwykły asystent (`/dobor-zestawu-pv?klient=<id>`), a nie w jego kopię: ten sam silnik i formularz,
 * tylko inne zakończenie. Zamiast prowadzić do wykonawcy, wybrany wariant zapisuje się na koncie i od razu
 * podpina pod klienta, a strona wraca do jego karty. Źródłem trybu jest parametr w adresie, więc przeżywa
 * odświeżenie i powrót „wstecz”; sesja asystenta (`lib/sesjaAsystenta`) pamięta formularz osobno dla adresu.
 *
 * Z karty bierzemy tylko to, co ma w asystencie jednoznaczne pole: moc PV (kWp) i pojemność magazynu (kWh).
 * Rodzaju instalacji (1F/3F) karta nie zapisuje, a województwo nie wpływa na dobór, więc ich nie zgadujemy.
 * Moc, gdy karta jej nie ma, podpowiada profil energetyczny klienta (`dane.energia`, 2026-10-14): ta sama reguła
 * doboru mocy co w kalkulatorze mocy (`mocZZuzycia`). Użytkownik może ją zmienić jak każde pole.
 */

/** Id klienta z adresu asystenta (`?klient=12`) albo null. */
export const klientZAdresu = (search) => {
  const m = String(new URLSearchParams(search).get('klient') || '').match(/^\d{1,9}$/)
  const id = m ? Number(m[0]) : 0
  return id > 0 ? id : null
}

/** Adres asystenta w trybie klienta; linki z karty klienta. */
export const adresAsystentaKlienta = (klientId) => `/dobor-zestawu-pv?klient=${encodeURIComponent(klientId)}`

/** Adres karty klienta, do której wracamy po zapisie. */
export const adresKartyKlienta = (klientId) => `/klienci/${encodeURIComponent(klientId)}`

/** Ten sam adres bez parametru `klient`: wyjście z trybu z zachowaniem reszty (np. `przypnij`). */
export const adresBezKlienta = (search) => {
  const q = new URLSearchParams(search)
  q.delete('klient')
  const reszta = q.toString()
  return `/dobor-zestawu-pv${reszta ? `?${reszta}` : ''}`
}

/** Liczba do pola formularza asystenta: kropka dziesiętna, bez zbędnych zer („8,20” -> „8.2”). */
const doPola = (x) => (x == null ? '' : String(+x.toFixed(3)))

/**
 * Wartości z karty klienta dla asystenta (odpowiedź `GET /klienci/{id}`).
 *
 * Moc: najpierw pole wpisane w karcie (`dane.mocPv`, z przecinkiem), potem moc z zużycia klienta
 * (`kwpZrodlo: 'zuzycie'`, razem z `zuzycieKwh`, żeby asystent mógł powiedzieć, skąd ją wziął), a na końcu to, co
 * policzył serwer (`moc_pv`: teczka PME2 albo podpięty zestaw, `kwpZrodlo: 'zestaw'`). Pojemność: pole karty, a gdy
 * puste, serwer, jak kafelki w karcie klienta.
 */
export const wartosciZKlienta = (klient) => {
  const d = klient?.dane || {}
  const kwh = liczbaZPola(d.pojemnosc) ?? liczbaZPola(klient?.pojemnosc?.wartosc)
  const zKarty = liczbaZPola(d.mocPv)
  const energia = energiaKlienta(d)
  const zuzycie = rocznyKwh(energia)
  const zZuzycia = zKarty == null ? mocZZuzycia(energia, { magazyn: kwh != null && kwh > 0 }) : null
  const zSerwera = zKarty == null && !zZuzycia ? liczbaZPola(klient?.moc_pv?.wartosc) : null
  const kwp = zKarty ?? zZuzycia?.mocKwp ?? zSerwera
  const zrodlo = zKarty != null ? 'karta' : (zZuzycia ? 'zuzycie' : (zSerwera != null ? 'zestaw' : ''))
  return {
    nazwa: String(klient?.nazwa || '').trim() || nazwaKlienta(d) || `Klient ${klient?.id ?? ''}`.trim(),
    kwp: doPola(kwp),
    kwh: doPola(kwh),
    ...(zrodlo ? { kwpZrodlo: zrodlo } : {}),
    ...(zZuzycia && zuzycie ? { zuzycieKwh: Math.round(zuzycie) } : {}),
  }
}

/**
 * Nazwa zapisywanego zestawu: nazwa z pola u góry asystenta (w trybie klienta podstawiona nazwa klienta)
 * i etykieta wariantu, żeby kilka wariantów jednego klienta dało się odróżnić na liście.
 */
export const nazwaZestawuKlienta = (nazwaWstepna, klientNazwa, variant) => {
  const kto = String(nazwaWstepna || '').trim() || String(klientNazwa || '').trim()
  const etykieta = String(variant?.variant_label_pl || '').trim() || 'Zestaw z asystenta'
  return [kto, etykieta].filter(Boolean).join(', ').slice(0, 120)
}

/**
 * Zapis wariantu na koncie i podpięcie pod klienta. Kształt zapisu jak w „Zapisz ten zestaw” (`SaveKitButton`):
 * cały wariant z silnika w `payload`, zapytanie doboru w `request_payload`, więc karta klienta pokazuje pozycje
 * z miniaturami i cenami, a zgłoszenia OSD i PSP biorą sprzęt jak z każdego zestawu.
 *
 * `zapisanyId`: zestaw zapisany przy poprzedniej próbie, której nie udało się podpiąć. Ponowienie tylko podpina,
 * zamiast zapisywać drugą kopię. Zwraca `{ ok, kitId, error }`.
 */
export const zapiszIPrzypisz = async ({ variant, requestPayload, nazwa, klientId, zapisanyId = null }) => {
  let kitId = zapisanyId
  if (!kitId) {
    const res = await saveKit({
      name: nazwa,
      payload: variant,
      request_payload: requestPayload || null,
      total_pln: (variant?.totals || {}).total_pln ?? null,
    })
    if (!res.ok) return { ok: false, kitId: null, error: res.error }
    kitId = res.data.id
  }
  const pod = await przypiszZestaw(kitId, klientId)
  if (!pod.ok) return { ok: false, kitId, error: `Zestaw zapisany na koncie, ale nie udało się przypisać go do klienta. ${pod.error}` }
  return { ok: true, kitId, error: null }
}
